import React, { useEffect, useRef, useState } from "react";
import {
  webLoadingFalse,
  webLoadingTrue,
  webNavigate,
} from "../../services/navigate.service";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import { coreFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from "../../services/fieldLimits";
import FieldCounter from "../../components/FieldCounter";
import {
  createDeviceUserRole,
  getDetailsDeviceUserRole,
  getDeviceUserRole,
  updateDeviceUserRole,
} from "../../services/deviceuserrole.service";
import { useResponsive } from "../../hook/useResponsive";
import { useFormKeyboard } from "../../hook/useFormKeyboard";
import { BORDER_SOFT, TEAL, TEXT, TEXT_DIM, TEXT_MUTED, formatUpdatedAt } from "../../components/list";
import { STICKY_FOOTER_HEIGHT, StickyFooter } from "../../components/mobile";
import { PromotionTendersDeviceRole, usePromotionOptions } from "./PromotionTendersDeviceRole";
import {
  DEVICE_FUNCTION_IDS,
  DEVICE_SECTIONS,
  DeviceForm,
  DeviceFunction,
  EMPTY_DEVICE_FORM,
  buildDevicePayload,
  nextSortOrder,
  splitAssignedPromotions,
  splitStoredFunctions,
  validateDeviceRole,
} from "./roleLogic";
import {
  BackLink,
  Badge,
  BadgeKind,
  FieldErrorText,
  FieldHint,
  H2,
  INK,
  Label,
  PANEL,
  PillButton,
  RoleDialog,
  RoleStyles,
  SURFACE_SOFT,
  Switch,
  TextButton,
  TriCheck,
  UnsavedBar,
  changeSummary,
  fieldStyle,
  textAreaStyle,
  useBeforeUnload,
  useHostLeaveGuard,
} from "./roleShared";
import { LeaveProceed } from "../../services/leaveGuard";

const BADGE_KINDS: Record<string, BadgeKind> = {
  New: "teal",
  "Coming soon": "off",
};

const DEVICE_DETAIL_LABELS: { key: keyof DeviceForm; label: string }[] = [
  { key: "roleName", label: "Name" },
  { key: "roleDescription", label: "Description" },
  { key: "roleHirerarchy", label: "Sort order" },
  { key: "restrictionDescription", label: "Notes" },
];

const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));

type LoadState = "loading" | "ready" | "error";

const DeviceUserRoleDetail = () => {
  const path = window.location.pathname;
  const id: string = path.split("/").pop() || "";
  const isNew = id === "new";
  const preLanguage = localStorage.getItem("language");
  const { isMobile, isTouchLayout } = useResponsive();
  const touch = isTouchLayout;
  const kb = useFormKeyboard({ autoFocus: isNew && !isMobile });
  const limits = useFieldLimits(coreFormLimits("device-user-role"));
  const promotions = usePromotionOptions();
  const [language, setLanguage] = useState<any>(null);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [role, setRole] = useState<any>(null);
  const [staffCount, setStaffCount] = useState<number | null>(null);
  const [initialForm, setInitialForm] = useState<DeviceForm>(EMPTY_DEVICE_FORM);
  const [form, setForm] = useState<DeviceForm>(EMPTY_DEVICE_FORM);
  const [initialChecked, setInitialChecked] = useState<string[]>([]);
  const [checked, setChecked] = useState<string[]>([]);
  const [unknownFunctions, setUnknownFunctions] = useState<string[]>([]);
  const [assignedPromotionIds, setAssignedPromotionIds] = useState<string[] | null>(null);
  const [initialPromos, setInitialPromos] = useState<string[]>([]);
  const [selectedPromos, setSelectedPromos] = useState<string[]>([]);
  const [hiddenPromos, setHiddenPromos] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [leaveTo, setLeaveTo] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);
  const leaveProceed = useRef<LeaveProceed>(null);

  const tr = (key: string) => (language && language[key] ? language[key] : key);
  const fill = (key: string, values: Record<string, string | number>) =>
    Object.keys(values).reduce((text, name) => text.split(`{${name}}`).join(String(values[name])), tr(key));

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /* ---------------- Loading ---------------- */

  const load = async (keepScreen = false) => {
    if (!keepScreen) setLoadState("loading");
    webLoadingTrue();
    try {
      // The list carries the staff count and the sort orders in use; it is optional here.
      const listPromise = getDeviceUserRole({ page: 1, limit: 1000, searchValue: "" })
        .then((response: any) => (Array.isArray(response?.data?.data?.deviceUserRole) ? response.data.data.deviceUserRole : null))
        .catch(() => null);
      let nextForm: DeviceForm = EMPTY_DEVICE_FORM;
      let stored: string[] = [];
      let assigned: string[] = [];
      if (!isNew) {
        const response: any = await getDetailsDeviceUserRole(id, "");
        const loaded = response?.data?.data;
        // core-api answers an unknown id with 404; an empty body still counts as not found.
        if (!loaded || !loaded.id) throw new Error("Device role not found");
        setRole(loaded);
        nextForm = {
          roleName: String(loaded.roleName ?? ""),
          roleDescription: String(loaded.roleDescription ?? ""),
          restrictionDescription: String(loaded.restrictionDescription ?? ""),
          roleHirerarchy: loaded.roleHirerarchy === null || loaded.roleHirerarchy === undefined ? "" : String(loaded.roleHirerarchy),
        };
        stored = (loaded.mapRole ?? []).map((m: any) => m?.functionId);
        assigned = (loaded.mapPromotion ?? [])
          .map((m: any) => m?.promotionId)
          .filter((promotionId: any) => typeof promotionId === "string" && promotionId.length > 0);
      }
      const list = await listPromise;
      if (isNew) nextForm = { ...EMPTY_DEVICE_FORM, roleHirerarchy: String(nextSortOrder(list ?? [])) };
      const row = list?.find((r: any) => String(r.id) === id);
      setStaffCount(isNew ? null : row ? Number(row.staffCount ?? 0) : null);
      const split = splitStoredFunctions(stored);
      setInitialForm(nextForm);
      setForm(nextForm);
      setInitialChecked(split.known);
      setChecked(split.known);
      setUnknownFunctions(split.unknown);
      setAssignedPromotionIds(assigned);
      setErrors({});
      setLoadState("ready");
    } catch (error) {
      console.error("Error loading device role:", error);
      setLoadState((current) => (keepScreen && current === "ready" ? current : "error"));
      if (keepScreen) showApiError(error, { language });
    } finally {
      webLoadingFalse();
    }
  };

  useEffect(() => {
    load();
    // A token refresh reloads the role, but never over unsaved edits.
    const handleNewToken = () => {
      if (!dirtyRef.current) load(true);
    };
    document.addEventListener("newToken", handleNewToken);
    return () => {
      document.removeEventListener("newToken", handleNewToken);
    };
  }, [path]);

  // Discounts count as loaded only once the role's ids AND the full list are in, and the
  // picker state is filled from them in the same update (never an empty first frame).
  const [promosLoaded, setPromosLoaded] = useState(false);
  useEffect(() => {
    if (assignedPromotionIds === null || promotions.status !== "ready") {
      setPromosLoaded(false);
      return;
    }
    const split = splitAssignedPromotions(assignedPromotionIds, promotions.options);
    setInitialPromos(split.selected);
    setSelectedPromos(split.selected);
    setHiddenPromos(split.hidden);
    setPromosLoaded(true);
  }, [assignedPromotionIds, promotions.status, promotions.options]);

  /* ---------------- Changes ---------------- */

  const changedDetails = DEVICE_DETAIL_LABELS.filter(({ key }) => initialForm[key].trim() !== form[key].trim()).map(({ label }) => tr(label));
  const added = DEVICE_FUNCTION_IDS.filter((fid) => checked.includes(fid) && !initialChecked.includes(fid));
  const removed = DEVICE_FUNCTION_IDS.filter((fid) => !checked.includes(fid) && initialChecked.includes(fid));
  const labelOf = (fid: string) => {
    const fn = DEVICE_SECTIONS.flatMap((s) => s.functions).find((f) => f.id === fid);
    return tr(fn?.label ?? fid);
  };
  const promosChanged = promosLoaded && !sameSet(initialPromos, selectedPromos);
  const changeItems = [
    ...changedDetails,
    ...added.map((fid) => "+" + labelOf(fid)),
    ...removed.map((fid) => "−" + labelOf(fid)),
    ...(promosChanged ? [tr("Discounts")] : []),
  ];
  const dirty = changeItems.length > 0;
  dirtyRef.current = dirty;
  useBeforeUnload(dirty, dirtyRef);
  // A company switch or other host leave while dirty opens the leave dialog instead.
  useHostLeaveGuard(dirtyRef, (proceed) => {
    leaveProceed.current = proceed;
    setLeaveTo("");
  });

  const setField = (name: keyof DeviceForm, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };
  const setFunctions = (ids: string[], on: boolean) =>
    setChecked((prev) => {
      const next = new Set(prev);
      ids.forEach((fid) => (on ? next.add(fid) : next.delete(fid)));
      return DEVICE_FUNCTION_IDS.filter((fid) => next.has(fid));
    });

  const discard = () => {
    setForm(initialForm);
    setChecked(initialChecked);
    setSelectedPromos(initialPromos);
    setErrors({});
  };

  const listPath = "/" + preLanguage + "/roles?tab=device";
  const guardedNavigate = (to: string) => {
    if (dirty) setLeaveTo(to);
    else webNavigate(to);
  };

  /* ---------------- Save ---------------- */

  const handleSave = async () => {
    if (saving) return;
    const limitErrors = validateAgainstLimits({ ...form }, limits, language);
    const ruleErrors = validateDeviceRole(form);
    const nextErrors: Record<string, string> = { ...limitErrors };
    Object.keys(ruleErrors).forEach((key) => {
      nextErrors[key] = tr(ruleErrors[key]);
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      if (nextErrors.roleHirerarchy || nextErrors.restrictionDescription) setShowAdvanced(true);
      return;
    }
    const payload = buildDevicePayload(
      form,
      { checked, keptUnknown: unknownFunctions },
      { loaded: promosLoaded, selectedIds: selectedPromos, hiddenAssignedIds: hiddenPromos },
    );
    setSaving(true);
    webLoadingTrue();
    try {
      if (isNew) {
        const response: any = await createDeviceUserRole(payload);
        const createdId = response?.data?.data?.id;
        showToast({ message: fill("{name} created", { name: form.roleName.trim() }), type: "success", duration: 4000 });
        dirtyRef.current = false;
        setInitialForm(form);
        setInitialChecked(checked);
        setInitialPromos(selectedPromos);
        webNavigate(createdId ? "/" + preLanguage + "/deviceUserRole/" + encodeURIComponent(String(createdId)) : listPath);
      } else {
        await updateDeviceUserRole(id, payload);
        showToast({ message: fill("{name} saved", { name: form.roleName.trim() }), type: "success", duration: 4000 });
        setInitialForm(form);
        setInitialChecked(checked);
        setInitialPromos(selectedPromos);
        await load(true);
      }
    } catch (error: any) {
      showApiError(error, { language, setFieldErrors: setErrors });
    } finally {
      setSaving(false);
      webLoadingFalse();
    }
  };

  /* ---------------- Rendering ---------------- */

  const errorFor = (name: string) => (errors[name] ? <FieldErrorText id={`device-${name}-error`}>{errors[name]}</FieldErrorText> : null);
  const describedBy = (name: string, hintId?: string) =>
    [errors[name] ? `device-${name}-error` : "", hintId ?? ""].filter(Boolean).join(" ") || undefined;
  const title = isNew ? form.roleName.trim() || tr("New device role") : initialForm.roleName || tr("Device role");
  const lastUpdated = !isNew && role?.updatedAt ? formatUpdatedAt(role.updatedAt, language) : "";

  const header = (
    <header style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <h1 style={{ margin: 0, fontSize: touch ? "22px" : "26px", fontWeight: 600, lineHeight: 1.25, color: INK, overflowWrap: "anywhere" }}>
          {title}
        </h1>
        {staffCount !== null && (
          <Badge label={staffCount === 1 ? tr("1 staff member") : fill("{n} staff", { n: staffCount })} kind="off" />
        )}
      </div>
      <p style={{ margin: 0, fontSize: "14px", color: TEXT_MUTED, fontVariantNumeric: "tabular-nums" }}>
        {lastUpdated ? `${fill("Last updated {date}", { date: lastUpdated })} · ` : ""}
        {tr("Tills pick up changes at their next sync.")}
      </p>
    </header>
  );

  const detailsPanel = (
    <section aria-labelledby="device-details-title" style={{ ...PANEL, padding: touch ? "16px" : "20px" }}>
      <h2 id="device-details-title" style={H2}>
        {tr("Details")}
      </h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: touch ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
          gap: touch ? "16px" : "20px 24px",
          marginTop: "16px",
        }}
      >
        <div>
          <Label htmlFor="device-roleName" required>
            {tr("Role name")}
          </Label>
          <input
            id="device-roleName"
            name="roleName"
            className="role-field"
            value={form.roleName}
            aria-invalid={!!errors.roleName || undefined}
            aria-describedby={describedBy("roleName")}
            {...limitProps(limits, "roleName")}
            onChange={(e) => setField("roleName", e.target.value)}
            style={fieldStyle(touch, !!errors.roleName)}
          />
          <FieldCounter value={form.roleName} max={limits.roleName?.maxLength} />
          {errorFor("roleName")}
        </div>
        <div>
          <Label htmlFor="device-roleDescription">{tr("Description")}</Label>
          <textarea
            id="device-roleDescription"
            name="roleDescription"
            className="role-field"
            value={form.roleDescription}
            aria-describedby={describedBy("roleDescription")}
            {...limitProps(limits, "roleDescription")}
            onChange={(e) => setField("roleDescription", e.target.value)}
            style={{ ...textAreaStyle(touch, !!errors.roleDescription), height: touch ? "88px" : "44px", minHeight: "44px" }}
          />
          <FieldCounter value={form.roleDescription} max={limits.roleDescription?.maxLength} />
          {errorFor("roleDescription")}
        </div>
      </div>
      <div style={{ marginTop: "16px", borderTop: `1px solid ${BORDER_SOFT}`, paddingTop: "12px" }}>
        <button
          type="button"
          className="role-disclosure"
          aria-expanded={showAdvanced}
          aria-controls="device-advanced"
          onClick={() => setShowAdvanced((v) => !v)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            minHeight: touch ? "44px" : "32px",
            padding: "0 4px",
            border: "none",
            borderRadius: "8px",
            background: "none",
            color: TEXT,
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          <span aria-hidden="true" style={{ display: "inline-block", transform: showAdvanced ? "rotate(90deg)" : "none", transition: "transform 150ms ease", color: TEXT_DIM }}>
            ›
          </span>
          {tr("Advanced: sort order and notes (not used by the till)")}
        </button>
        {showAdvanced && (
          <div
            id="device-advanced"
            style={{
              display: "grid",
              gridTemplateColumns: touch ? "1fr" : "minmax(160px, 220px) 1fr",
              gap: touch ? "16px" : "20px 24px",
              marginTop: "12px",
            }}
          >
            <div>
              <Label htmlFor="device-roleHirerarchy" required>
                {tr("Sort order")}
              </Label>
              <input
                id="device-roleHirerarchy"
                name="roleHirerarchy"
                className="role-field"
                inputMode="numeric"
                value={form.roleHirerarchy}
                aria-invalid={!!errors.roleHirerarchy || undefined}
                aria-describedby={describedBy("roleHirerarchy", "device-sort-hint")}
                onChange={(e) => setField("roleHirerarchy", e.target.value.replace(/[^\d]/g, "").slice(0, 3))}
                style={fieldStyle(touch, !!errors.roleHirerarchy)}
              />
              <FieldHint id="device-sort-hint">{tr("Orders the roles in lists. The till does not use it.")}</FieldHint>
              {errorFor("roleHirerarchy")}
            </div>
            <div>
              <Label htmlFor="device-restrictionDescription">{tr("Notes for managers")}</Label>
              <textarea
                id="device-restrictionDescription"
                name="restrictionDescription"
                className="role-field"
                value={form.restrictionDescription}
                aria-describedby={describedBy("restrictionDescription", "device-notes-hint")}
                {...limitProps(limits, "restrictionDescription")}
                onChange={(e) => setField("restrictionDescription", e.target.value)}
                style={textAreaStyle(touch, !!errors.restrictionDescription)}
              />
              <FieldCounter value={form.restrictionDescription} max={limits.restrictionDescription?.maxLength} />
              <FieldHint id="device-notes-hint">{tr("Not enforced. Only shown here.")}</FieldHint>
              {errorFor("restrictionDescription")}
            </div>
          </div>
        )}
      </div>
    </section>
  );

  const functionRow = (fn: DeviceFunction, index: number) => {
    const on = !fn.comingSoon && checked.includes(fn.id);
    const nameId = `device-fn-${fn.id.replace(/[^a-z0-9]/gi, "-")}`;
    const descId = `${nameId}-desc`;
    return (
      <div
        key={fn.id}
        className={fn.comingSoon ? undefined : "role-row"}
        onClick={() => !fn.comingSoon && setFunctions([fn.id], !on)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          minHeight: touch ? "56px" : "48px",
          padding: "10px 12px",
          borderTop: index === 0 ? "none" : `1px solid ${BORDER_SOFT}`,
          cursor: fn.comingSoon ? "default" : "pointer",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <span id={nameId} style={{ fontSize: touch ? "15px" : "14px", fontWeight: 500, color: fn.comingSoon ? TEXT_DIM : TEXT }}>
              {tr(fn.label ?? fn.id)}
            </span>
            {(fn.badges ?? []).map((badge) => (
              <Badge key={badge} label={tr(badge)} kind={BADGE_KINDS[badge] ?? "warn"} />
            ))}
          </div>
          {(fn.description || fn.tillName) && (
            <p id={descId} style={{ margin: "2px 0 0", fontSize: "13px", lineHeight: "18px", color: TEXT_DIM }}>
              {fn.description ? tr(fn.description) : ""}
              {fn.description && fn.tillName ? " " : ""}
              {fn.tillName ? tr(fn.tillName) + "." : ""}
            </p>
          )}
        </div>
        <Switch
          checked={on}
          disabled={!!fn.comingSoon}
          label={tr(fn.label ?? fn.id)}
          describedBy={fn.description || fn.tillName ? descId : undefined}
          touch={touch}
          onChange={(next) => setFunctions([fn.id], next)}
        />
      </div>
    );
  };

  const sectionsPanel = DEVICE_SECTIONS.map((section) => {
    const grantable = section.functions.filter((f) => !f.comingSoon).map((f) => f.id);
    const onCount = grantable.filter((fid) => checked.includes(fid)).length;
    const state = onCount === 0 ? "none" : onCount === grantable.length ? "all" : "some";
    return (
      <section key={section.key} aria-labelledby={`device-section-${section.key}`} style={{ ...PANEL, padding: touch ? "16px" : "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <TriCheck
            state={state}
            label={fill("All functions in {section}", { section: tr(section.title) })}
            onChange={(next) => setFunctions(grantable, next)}
          />
          <h2 id={`device-section-${section.key}`} style={H2}>
            {tr(section.title)}
          </h2>
          <span style={{ fontSize: "13px", color: TEXT_DIM, fontVariantNumeric: "tabular-nums" }}>
            {fill("{n} of {m}", { n: onCount, m: grantable.length })}
          </span>
          <span style={{ marginLeft: "auto", display: "inline-flex", gap: "2px" }}>
            <TextButton onClick={() => setFunctions(grantable, true)} disabled={state === "all"}>
              {tr("All")}
            </TextButton>
            <TextButton onClick={() => setFunctions(grantable, false)} disabled={state === "none"}>
              {tr("None")}
            </TextButton>
          </span>
        </div>
        <div style={{ marginTop: "12px", border: `1px solid ${BORDER_SOFT}`, borderRadius: "12px", overflow: "hidden" }}>
          {section.functions.map(functionRow)}
        </div>
      </section>
    );
  });

  const otherFunctions = unknownFunctions.length > 0 && (
    <section aria-labelledby="device-other-title" style={{ ...PANEL, padding: touch ? "16px" : "20px" }}>
      <h2 id="device-other-title" style={H2}>
        {tr("Other functions set elsewhere")}
      </h2>
      <p style={{ margin: "4px 0 12px", fontSize: "14px", color: TEXT_MUTED }}>
        {tr("This page does not manage these. They are kept as they are when you save.")}
      </p>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {unknownFunctions.map((fid) => (
          <li key={fid}>
            <Badge label={fid} kind="off" />
          </li>
        ))}
      </ul>
    </section>
  );

  const discounts = (
    <PromotionTendersDeviceRole
      language={language}
      touch={touch}
      status={promotions.status}
      options={promotions.options}
      selectedIds={selectedPromos}
      hiddenCount={hiddenPromos.length}
      onChange={setSelectedPromos}
      onRetry={promotions.retry}
    />
  );

  const saveLabel = isNew ? tr("Create role") : tr("Save changes");
  const firstError = Object.values(errors).find(Boolean) ?? "";

  const leaveDialog = (
    <RoleDialog
      open={leaveTo !== null}
      touch={touch}
      language={language}
      title={tr("Leave without saving?")}
      onClose={() => setLeaveTo(null)}
      actions={[
        { label: tr("Keep editing"), onClick: () => setLeaveTo(null) },
        {
          label: tr("Discard changes"),
          variant: "danger",
          onClick: () => {
            const to = leaveTo;
            const proceed = leaveProceed.current;
            leaveProceed.current = null;
            setLeaveTo(null);
            dirtyRef.current = false;
            if (to) webNavigate(to);
            else {
              discard();
              proceed?.();
            }
          },
        },
      ]}
    >
      <p style={{ margin: 0 }}>{fill("Your {n} unsaved changes to this role will be lost.", { n: changeItems.length })}</p>
    </RoleDialog>
  );

  const root = (children: React.ReactNode) => (
    <div {...kb.scopeProps}>
      <div
        className="h-full font-inter scroll-container"
        style={{ height: "100%", overflowY: "auto", fontFamily: "Inter, sans-serif", color: TEXT, display: "flex", flexDirection: "column" }}
      >
        <RoleStyles />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: touch ? "16px" : "20px",
            paddingBottom: touch ? `${STICKY_FOOTER_HEIGHT + 16}px` : "8px",
            minWidth: 0,
            flex: 1,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );

  const backLink = <BackLink label={tr("Back to roles")} onClick={() => guardedNavigate(listPath)} />;

  if (loadState !== "ready") {
    return root(
      <>
        {backLink}
        {loadState === "error" ? (
          <div style={{ ...PANEL, padding: "24px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px" }}>
            <p style={{ margin: 0, fontSize: "15px", fontWeight: 500, color: TEXT }}>{tr("This role could not be loaded.")}</p>
            <PillButton small onClick={() => load()} style={{ color: TEAL }}>
              {tr("Try again")}
            </PillButton>
          </div>
        ) : (
          <div aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {[44, 180, 320].map((h) => (
              <div key={h} style={{ height: `${h}px`, borderRadius: "16px", backgroundColor: SURFACE_SOFT, border: `1px solid ${BORDER_SOFT}` }} />
            ))}
          </div>
        )}
      </>,
    );
  }

  const body = (
    <>
      {backLink}
      {header}
      {touch && dirty && (
        <p style={{ margin: 0, fontSize: "13px", color: TEXT_MUTED, display: "flex", alignItems: "center", gap: "8px" }}>
          <span>{changeItems.length === 1 ? tr("1 unsaved change") : fill("{n} unsaved changes", { n: changeItems.length })}</span>
          <TextButton onClick={discard}>{tr("Discard")}</TextButton>
        </p>
      )}
      {touch && firstError && <FieldErrorText>{firstError}</FieldErrorText>}
      {detailsPanel}
      {sectionsPanel}
      {discounts}
      {otherFunctions}
    </>
  );

  if (touch) {
    return (
      <>
        {root(body)}
        <StickyFooter
          cancelLabel={tr("Cancel")}
          onCancel={() => guardedNavigate(listPath)}
          primaryLabel={saveLabel}
          primaryDisabled={!dirty || saving}
          onPrimary={() => {
            void handleSave();
          }}
        />
        {leaveDialog}
      </>
    );
  }

  return (
    <>
      {root(
        <>
          {body}
          <UnsavedBar
            language={language}
            dirty={dirty}
            count={changeItems.length}
            summary={changeSummary(changeItems, language)}
            error={firstError}
            onDiscard={discard}
            saveButton={
              <button
                type="button"
                className="role-primary"
                {...kb.primaryProps(handleSave)}
                disabled={!dirty || saving}
                style={{
                  height: "40px",
                  padding: "0 24px",
                  borderRadius: "999px",
                  border: "none",
                  backgroundColor: TEAL,
                  color: "#FFFFFF",
                  fontSize: "14px",
                  fontWeight: 500,
                  opacity: !dirty || saving ? 0.5 : 1,
                  cursor: !dirty || saving ? "not-allowed" : "pointer",
                }}
              >
                {saveLabel}
              </button>
            }
          />
        </>,
      )}
      {leaveDialog}
    </>
  );
};
export default DeviceUserRoleDetail;
